import React, { useEffect, useState, useMemo } from "react";
import NoDataFound from "../../common/NoDataFound";
import { loadStyles } from "../../../utils/cssLoader";
import { useSocket } from "../../../hooks/useSocket";
import { useFootballSocket } from "../../../hooks/useFootballSocket";
import LeagueHead from "./LeagueHead";
import LeagueBody from "./LeagueBody";

// Helper to get first N matches for initial load
const firstCountMatches = (data, count = 15) => {
  if (!data || !Array.isArray(data)) return [];

  let matchCount = 0;
  const result = [];

  for (const league of data) {
    if (matchCount >= count) break;

    const leagueMatches = league?.matches || [];
    const remainingCount = count - matchCount;

    if (leagueMatches.length <= remainingCount) {
      result.push(league);
      matchCount += leagueMatches.length;
    } else {
      result.push({
        ...league,
        matches: leagueMatches.slice(0, remainingCount),
      });
      matchCount = count;
    }
  }

  return result;
};

const LeagueMatches = ({
  finalData,
  difference,
  matchType,
  loadCSS,
  setDataId,
  setDataType,
  setHead,
  dataClient,
  websiteBaseUrl,
  dataPath = "football",
  updatedData,
  Result = false,
  teamid,
  sport = "football",
  socketUrl = "",
  channel = "IPUB",
  event = "FootballLiveMatches",
  type,
  baseUrl,
  allowedLeagues,
  design,
  initialMatchCount = 15,
  lazyLoad = true,
}) => {
  const baseURL = process.env.REACT_APP_BASE_URL;

  // Initial paginated data for performance
  const initialData = useMemo(() => {
    if (!lazyLoad) return finalData;
    return firstCountMatches(finalData, initialMatchCount);
  }, [finalData, initialMatchCount, lazyLoad]);

  const [data, setData] = useState(initialData);
  const [isFullDataLoaded, setIsFullDataLoaded] = useState(!lazyLoad);
  const socket = useSocket(socketUrl);
  useFootballSocket({ socket, channel, event, setData, type, allowedLeagues });

  // Update data when finalData changes
  useEffect(() => {
    if (isFullDataLoaded || !lazyLoad) {
      setData(finalData);
    } else {
      setData(firstCountMatches(finalData, initialMatchCount));
    }
  }, [finalData, isFullDataLoaded, initialMatchCount, lazyLoad]);

  // Load full data on scroll for lazy loading
  useEffect(() => {
    if (!lazyLoad || isFullDataLoaded) return;

    const handleScroll = () => {
      if (window.scrollY > 0 && !isFullDataLoaded) {
        setData(finalData);
        setIsFullDataLoaded(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFullDataLoaded, finalData, lazyLoad]);

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("leaguepage");
    }
  }, [loadCSS]);

  // Separate upcoming and results if needed (must be before early return)
  const upcomingMatches = useMemo(() => {
    if (matchType !== "all") return null;
    return data?.filter((league) =>
      league?.matches?.some((match) => {
        const status = match?.st?.toLowerCase();
        return status === "ns" || status === "not started" || status === "scheduled";
      })
    );
  }, [data, matchType]);

  const resultMatches = useMemo(() => {
    if (matchType !== "all") return null;
    return data?.filter((league) =>
      league?.matches?.some((match) => {
        const status = match?.st?.toLowerCase();
        return status === "ft" || status === "finished" || status === "ended" || status === "aet" || status === "pen";
      })
    );
  }, [data, matchType]);

  if (!data || data?.length === 0) {
    return (
      <div className="leg_wrp">
        <div className="leg_box">
          <div className="text-center">
            <NoDataFound
              text={
                type === "live"
                  ? "There are no live games currently in progress"
                  : matchType === "upcoming"
                  ? "No upcoming matches found"
                  : matchType === "results"
                  ? "No results found"
                  : "No matches found"
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // Render a league section
  const renderLeagueSection = (leagues, sectionTitle) => (
    <>
      {sectionTitle && leagues?.length > 0 && (
        <div className="leg_section_head">
          <h2>{sectionTitle}</h2>
        </div>
      )}
      {leagues?.map((league) => (
        <React.Fragment key={league?.id}>
          <LeagueHead
            league={league}
            updatedData={updatedData}
            setDataType={setDataType}
            setDataId={setDataId}
            setHead={setHead}
            baseURL={baseURL}
            matchType={matchType}
            websiteBaseUrl={websiteBaseUrl}
            dataClient={dataClient}
            dataPath={dataPath}
            design={design}
          />
          {Array.isArray(league?.matches) &&
            league.matches.map((match) => (
              <LeagueBody
                key={match?.id}
                match={match}
                matchType={matchType}
                updatedData={updatedData}
                difference={difference}
                Result={Result}
                sport={sport}
                websiteBaseUrl={websiteBaseUrl}
                dataClient={dataClient}
                dataPath={dataPath}
                teamid={teamid}
                design={design}
              />
            ))}
        </React.Fragment>
      ))}
    </>
  );

  return (
    <div className="leg_wrp">
      <div className="leg_box">
        {matchType === "all" ? (
          <>
            {/* Show upcoming and results separately when matchType is "all" */}
            {upcomingMatches?.length > 0 && (
              <div className="leg_section upcoming_section">
                {renderLeagueSection(upcomingMatches, "Upcoming Matches")}
              </div>
            )}
            {resultMatches?.length > 0 && (
              <div className="leg_section results_section">
                {renderLeagueSection(resultMatches)}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Show section header for specific match types */}
            {matchType === "upcoming" && !data[0]?.nm && (
              <div className="leg_head">
                <h2>
                  <span className="name">Upcoming Matches</span>
                </h2>
              </div>
            )}
            {matchType === "results" && !data[0]?.nm && (
              <div className="leg_head">
                <h2>
                  <span className="name">Results</span>
                </h2>
              </div>
            )}
            {renderLeagueSection(data)}
          </>
        )}
      </div>
    </div>
  );
};

export default LeagueMatches;
